import React, { useState, useEffect, useCallback } from 'react';
import { customerAPI } from '../api';
import { Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, X, Users, Upload, FileSpreadsheet, RefreshCw } from 'lucide-react';

const emptyForm = { customer_code: '', customer_name: '', payment_terms_code: '' };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bcCompanies, setBcCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [showSyncModal, setShowSyncModal] = useState(false);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await customerAPI.getAll({ page, limit: 15, search });
      setCustomers(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch('/api/bc/companies', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setBcCompanies(data.companies || []);
        if (data.companies && data.companies.length > 0) {
          setSelectedCompany(data.companies[0]);
        }
      })
      .catch(err => console.error('Failed to fetch BC companies:', err));
  }, []);

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editId) {
        await customerAPI.update(editId, form);
      } else {
        await customerAPI.create(form);
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditId(null);
      load(pagination.page);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  function openEdit(row) {
    setForm({ customer_code: row.customer_code || '', customer_name: row.customer_name || '', payment_terms_code: row.payment_terms_code || '' });
    setEditId(row.id);
    setShowModal(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await customerAPI.delete(id);
      load(pagination.page);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  async function handleBulkUpload(e) {
    e.preventDefault();
    if (!uploadFile) {
      alert('Please select an Excel file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const res = await fetch('/api/customers/bulk-upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      alert(`Upload successful!\n\nTotal: ${data.total} customers\nInserted: ${data.inserted}\nSkipped (duplicates): ${data.skipped}`);
      setShowUploadModal(false);
      setUploadFile(null);
      load(1);
    } catch (err) {
      alert('Upload error: ' + err.message);
    }
    setUploading(false);
  }

  async function handleSyncFromBC() {
    if (!selectedCompany) {
      alert('Please select a BC company');
      return;
    }

    setSyncing(true);
    try {
      const res = await fetch('/api/bc/sync-customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company: selectedCompany })
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Sync failed');
      }

      alert(`Sync successful!\n\nFetched: ${data.totalFetched} customers from BC\nUpdated: ${data.totalUpdated} payment terms`);
      setShowSyncModal(false);
      load(1);
    } catch (err) {
      alert('Sync error: ' + err.message);
    }
    setSyncing(false);
  }

  async function handleCleanupNonAR() {
    if (!window.confirm('This will delete ALL customers that are NOT in AR Posting Group.\n\nOnly 2,792 AR customers will be kept.\nProceed?')) return;
    
    try {
      const res = await fetch('/api/bc/cleanup-non-ar', { method: 'POST', credentials: 'include' });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Cleanup failed');
      }

      alert(`${data.message}\n\nKept: ${data.kept} AR customers\n\nSample deletions:\n${data.sampleDeletions.join('\n')}`);
      load(1);
    } catch (err) {
      alert('Cleanup error: ' + err.message);
    }
  }

  return (
    <div>
      <div className="table-container">
        <div className="table-header table-title-row">
          <h3>Customers ({pagination.total.toLocaleString()} total)</h3>
        </div>

        <div className="table-filters-row">
          <div className="table-search-wrap">
            <Search size={16} className="table-search-icon" />
            <input
              className="search-input table-search-input"
              placeholder="Search customer name or code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setEditId(null); setShowModal(true); }}>
            <Plus size={16} /> Add Customer
          </button>
          <button className="btn btn-primary" onClick={() => setShowUploadModal(true)} style={{ background: '#16a34a' }}>
            <FileSpreadsheet size={16} /> Upload Excel
          </button>
          <button className="btn btn-secondary" onClick={handleCleanupNonAR} title="Delete all non-AR customers">
            <Trash2 size={16} /> Cleanup Non-AR
          </button>
          <button className="btn btn-primary" onClick={() => setShowSyncModal(true)} style={{ background: '#7c3aed' }}>
            <RefreshCw size={16} /> Sync from BC (AR Only)
          </button>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Customer Code</th>
                <th>Customer Name</th>
                <th>Payment Terms</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40 }}>No customers found</td></tr>
              ) : customers.map((c, i) => (
                <tr key={c.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td style={{ fontFamily: 'monospace' }}>{c.customer_code}</td>
                  <td>{c.customer_name}</td>
                  <td>
                    {c.payment_terms_code ? (
                      <span style={{ padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 4, fontSize: 12, fontWeight: 500 }}>
                        {c.payment_terms_code}
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)} title="Edit"><Edit2 size={14} /></button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          Page {pagination.page} of {pagination.totalPages}
        </div>
        <div className="pagination-buttons">
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}><ChevronLeft size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)}><ChevronRight size={16} /></button>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editId ? 'Edit Customer' : 'New Customer'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Customer Code</label>
                    <input name="customer_code" value={form.customer_code} onChange={handleChange} placeholder="e.g. 01-00-001" />
                  </div>
                  <div className="form-group">
                    <label>Customer Name</label>
                    <input name="customer_name" value={form.customer_name} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Payment Terms Code</label>
                    <input name="payment_terms_code" value={form.payment_terms_code} onChange={handleChange} placeholder="e.g. NET30" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3><FileSpreadsheet size={20} /> Bulk Upload Customers</h3>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleBulkUpload}>
              <div className="modal-body">
                <div style={{ marginBottom: 16, padding: 16, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#0369a1' }}>
                    <strong>Excel Format:</strong> Your file must have these columns:
                  </p>
                  <ul style={{ margin: '8px 0 0 20px', fontSize: 13, color: '#0369a1' }}>
                    <li><code>customer_code</code> - Customer code (e.g., 01-00-001)</li>
                    <li><code>customer_name</code> - Customer name</li>
                  </ul>
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
                    Supported formats: .xlsx, .xls, .csv (max 10MB)
                  </p>
                </div>

                <div className="form-group">
                  <label>Select Excel File</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={e => setUploadFile(e.target.files[0])}
                    required
                    style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, width: '100%' }}
                  />
                  {uploadFile && (
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#16a34a' }}>
                      Selected: {uploadFile.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={uploading || !uploadFile}>
                  <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sync from BC Modal */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
            <div className="modal-header">
              <h3><RefreshCw size={20} /> Sync Payment Terms from BC</h3>
              <button className="modal-close" onClick={() => setShowSyncModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 16, padding: 16, background: '#f5f3ff', borderRadius: 8, border: '1px solid #ddd6fe' }}>
                <p style={{ margin: 0, fontSize: 13, color: '#6d28d9' }}>
                  <strong>What this does:</strong> Fetches all customers from Business Central and updates their Payment Terms Code in the local database.
                </p>
              </div>

              <div className="form-group">
                <label>Select BC Company</label>
                <select
                  value={selectedCompany}
                  onChange={e => setSelectedCompany(e.target.value)}
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                >
                  {bcCompanies.length === 0 ? (
                    <option value="">Loading companies...</option>
                  ) : (
                    bcCompanies.map(c => <option key={c} value={c}>{c}</option>)
                  )}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowSyncModal(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSyncFromBC}
                disabled={syncing || !selectedCompany}
                style={{ background: '#7c3aed' }}
              >
                <RefreshCw size={16} /> {syncing ? 'Syncing...' : 'Start Sync'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
